import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiFetch } from '../lib/apiFetch';

export interface SalesTotals {
  bookings: number;
  tickets: number;
  grossCents: number;
  subtotalCents: number;
}

export interface DailySalesDay {
  /** Eastern-time calendar date, YYYY-MM-DD. */
  date: string;
  /** Null when the warehouse could not be read. */
  fareharbor: SalesTotals | null;
  /** Null when the warehouse could not be read. */
  giftCards: SalesTotals | null;
  nebari: SalesTotals;
}

/** Eastern-time calendar dates, YYYY-MM-DD, both included. */
export interface SalesDateRange {
  from: string;
  to: string;
}

export interface DailySalesReport extends SalesDateRange {
  today: string;
  /** The day the single-day summary figures describe: yesterday when the range runs to
   *  today, otherwise its last day. */
  kpiDay: string;
  /** The same weekday a week before `kpiDay`, even when it falls before `from`. */
  kpiWeekBefore: DailySalesDay | null;
  generatedAt: string;
  /** The newest FareHarbor sale in the window, not when the warehouse last refreshed. */
  fareharborLatestSale: string | null;
  nebariLatestSale: string | null;
  days: DailySalesDay[];
}

export class DailySalesError extends Error {
  constructor(
    readonly status: number,
    readonly serverMessage?: string,
  ) {
    super(DailySalesError.messageFor(status, serverMessage));
  }

  private static messageFor(status: number, serverMessage?: string): string {
    if (status === 400 && serverMessage) {
      return `Those dates can't be shown: ${serverMessage}.`;
    }
    if (status === 403) {
      return 'Your role does not include the Daily sales report. Ask an admin if you need it.';
    }
    return 'Daily sales could not be loaded. Try again in a minute.';
  }

  get isRefusedDates(): boolean {
    return this.status === 400;
  }

  /** The server's reason for refusing the dates, as a sentence; null for other failures. */
  get refusedReason(): string | null {
    if (!this.isRefusedDates || !this.serverMessage) return null;
    return `${this.serverMessage.charAt(0).toUpperCase()}${this.serverMessage.slice(1)}.`;
  }
}

async function fetchDailySales(range: SalesDateRange | null): Promise<DailySalesReport> {
  const query = range ? `?${new URLSearchParams({ from: range.from, to: range.to })}` : '';
  const res = await apiFetch(`/api/v1/finance/daily-sales${query}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new DailySalesError(res.status, body?.message);
  }
  const body = (await res.json()) as { data: DailySalesReport };
  return body.data;
}

/** The report for `range`, or the default window when null. The previous range's
 *  report stays in place while a new one loads. */
export function useDailySales(range: SalesDateRange | null) {
  return useQuery({
    queryKey: ['finance', 'daily-sales', range?.from ?? null, range?.to ?? null],
    queryFn: () => fetchDailySales(range),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: (count, error) =>
      !(error instanceof DailySalesError && error.status >= 400 && error.status < 500) && count < 2,
  });
}
